import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FlightSearchActions } from "@b2bportal/core-flights";
import type { AppDispatch } from "@b2bportal/core-types";
import { CallState } from "@b2bportal/core-types";
import { PACKAGES_QUERY_PARAM } from "@tenant/packages";
import type { PackageSearchQueryParams } from "@tenant/types";
import { createFlightSearch } from "@/pages/Packages/hooks";
import {
  fetchPackageFlights,
  initializePackages,
  PackageBaseSelectors,
} from "@/store";
import { FlightShopActions } from "@/store/flights/shop/store";
import {
  PackageSearchSelectors,
  PackageFlightSelectors,
} from "@/store/packages/selectors";
import { PackagesBaseActions } from "@/store/packages/slice/packages";
import { fetchPackageSearchParams } from "@/store/packages/thunks/general";
import { parsePackageUpSellAttribution } from "@/tracking/upSell/packageUpSellParams";

export const usePackageFlightDeparturePageFetch = (
  props: PackageSearchQueryParams
) => {
  const dispatch = useDispatch<AppDispatch>();
  const hasInitialized = useRef(false);
  const fetchingTokenRef = useRef<string | null>(null);
  // The flight shop still loading, null once it settled. Aborted when the
  // page is left so a late response cannot refill a shop a new search has
  // cleared.
  const activeShopRef = useRef<{ abort: () => void } | null>(null);
  // The search params lookup resolves after the page may be gone; a shop
  // started then would have nothing to abort it.
  const isMountedRef = useRef(true);

  const packageToken = useSelector(
    PackageBaseSelectors.getCurrentActivePackageToken
  );

  const requestedSearch = useSelector(
    PackageSearchSelectors.getRequestedSearch
  );

  const initializedState = useSelector(
    PackageSearchSelectors.getInitializedState
  );

  const packageType = useSelector(
    PackageBaseSelectors.getCurrentActivePackageType
  );

  const flightShopCallState = useSelector(
    PackageFlightSelectors.getFlightShopCallState
  );

  // Package flight data, not the core flight shop's: the flights -> "add a
  // stay" up-sell leaves non-package results in the core store, and skipping
  // on those leaves packageStateByFareId empty so the return page can't
  // resolve a package for the chosen fare.
  const existingPackageFlightData = useSelector(
    PackageFlightSelectors.getPackageStateByFareId
  );

  // Shop fixed-points (grid) fares only when the pre-selected flight was itself
  // priced as fixed. That intent rides the URL as `up_sell_price_tab` (set from
  // the standalone shop's pricing mode) and survives navigation into the package
  // funnel and the checkout "change flight" path; a plain package search has no
  // tab and correctly defaults to flex. The BFF still returns flex per trip for
  // routes/fares that aren't grid-eligible.
  const isFixedPreselected =
    parsePackageUpSellAttribution(window.location.search).rbc_price_tab ===
    "fixed";

  useEffect(() => {
    // First, check if we need to restore token from URL
    if (!packageToken) {
      const urlToken = new URLSearchParams(window.location.search).get(
        PACKAGES_QUERY_PARAM.packageToken
      );
      if (urlToken) {
        // Restore token from URL and exit - effect will re-run with the token
        dispatch(PackagesBaseActions.setCurrentActivePackageToken(urlToken));
        return;
      }

      // Only requisition if there's no token in state OR URL
      if (hasInitialized.current || initializedState === CallState.InProcess) {
        return;
      }
      hasInitialized.current = true;

      // we don't have a package token in state or the url, do a requisition
      dispatch(initializePackages({ search: props }));
      return;
    }

    // Reset the flag when we have a token (allows re-initialization if token is cleared)
    hasInitialized.current = false;

    // Prevent fetching if we already have flight data (e.g., navigating back from return page)
    // or if we've already initiated a fetch with this token
    // or if a flight shop fetch is already in progress
    if (
      existingPackageFlightData != null ||
      fetchingTokenRef.current === packageToken ||
      flightShopCallState === CallState.InProcess
    ) {
      return;
    }

    fetchingTokenRef.current = packageToken;

    const startShop = (flightSearch: ReturnType<typeof createFlightSearch>) => {
      dispatch(FlightSearchActions.setFlightSearch(flightSearch));
      const shop = dispatch(
        fetchPackageFlights({
          props: {
            packageToken,
            search: {
              ...flightSearch,
              searchFixedPointsFares: isFixedPreselected,
            },
          },
        })
      );
      activeShopRef.current = shop;
      const settled = () => {
        if (activeShopRef.current === shop) {
          activeShopRef.current = null;
        }
      };
      shop.unwrap().then(settled, () => {
        // Reset on error to allow retries
        fetchingTokenRef.current = null;
        settled();
      });
    };

    if (requestedSearch?.origin && requestedSearch?.destination) {
      // verify packageType
      if (props.packageType !== packageType) {
        dispatch(
          PackagesBaseActions.setCurrentActivePackageType(props.packageType)
        );
      }

      startShop(createFlightSearch(requestedSearch));
    } else {
      // We don't have requestedSearch, let's get them
      dispatch(fetchPackageSearchParams(props))
        .unwrap()
        .then((search) => {
          if (!isMountedRef.current) {
            return;
          }
          startShop(createFlightSearch(search));
        })
        .catch(() => {
          // Reset on error to allow retries
          fetchingTokenRef.current = null;
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageToken]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      const shop = activeShopRef.current;
      if (shop) {
        shop.abort();
        // The thunk marked the core flight shop loading before its request.
        dispatch(FlightShopActions.setFetchFlightsAbandoned());
      }
    };
  }, [dispatch]);
};
